"use client";

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useSession } from "next-auth/react";
import { usePostHog } from "posthog-js/react";

type IdentityScope = { depth: number; owners: Map<symbol, number> };
const IdentityScopeContext = createContext<IdentityScope | null>(null);

/** Sync from the session that owns the current route, not a stale ancestor. */
export default function PostHogSessionIdentity({
  children,
}: PropsWithChildren) {
  const parent = useContext(IdentityScopeContext);
  const scope = useMemo(
    () => ({
      depth: (parent?.depth ?? -1) + 1,
      owners: parent?.owners ?? new Map<symbol, number>(),
    }),
    [parent]
  );
  const owner = useMemo(() => Symbol("session-identity"), []);
  const { data: session, status } = useSession();
  const posthog = usePostHog();
  const email = session?.user?.email;
  const userId = session?.user?.id;

  // This boundary wraps its children: nested scopes register before ancestors
  // run their effects. Even a loading/null child owns the route's identity.
  useEffect(() => {
    scope.owners.set(owner, scope.depth);
    return () => {
      scope.owners.delete(owner);
    };
  }, [owner, scope]);

  useEffect(() => {
    if (status !== "authenticated" || !email || !userId || !posthog) return;
    if (Array.from(scope.owners.values()).some((depth) => depth > scope.depth))
      return;
    posthog.identify(email, { email, user_id: userId });
    posthog.setPersonPropertiesForFlags({
      email,
      user_id: userId,
      platform: "web",
    });
    // Logout handlers own reset. An outer provider can report unauthenticated
    // while the inner provider is signed in; resetting here would undo it.
  }, [email, userId, status, posthog, scope]);

  return (
    <IdentityScopeContext.Provider value={scope}>
      {children}
    </IdentityScopeContext.Provider>
  );
}
